import React, { useState, useEffect, useContext } from "react"
import { Formik, Form, useField } from "formik"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { DateTime } from "luxon"

//primereact
import { Editor as InputTextareaRich } from "primereact/editor"

//graphQL
import { useLazyQuery, useQuery } from "@apollo/client"
import {
  REFERRAL_BY_IDSTAMP,
  REFERRAL_BY_IDSTAMP_NO_PARTICIPANT,
  REFERRAL_EXTRA_DETAIL,
} from "../queries"

// Context
import { UserContext } from "../../../context/userContext"

//Style
import styles from "../../../styles/Form-Inputs.module.css"
import refStyles from "../../../styles/Referrals.module.css"

//Routes
import ROUTES from "../../../components/Routes/routesContants"

//Referal modules
import RefTextInput from "../../Partner/Components/Input/RefTextInput"
import { refApiRefEntityStatusGet, RefApiRefRatingGet } from "../ReferralsAPI"
import { TabPanel, TabView } from "primereact/tabview"
import ReferralNotes from "./ReferralNotes"
import ReferralEntityStatusPopup from "./ReferralEntityStatusPopup"
import ReferralStatusPopup from "./ReferralStatusPopup"
import { Button } from "primereact/button"
import { getLocalDateTimeFromIso } from "./sharedFunctions"
import ReferralRating from "./ReferralRating"

//helpers
import { LogError } from "../../../helpers/logger"

export default function ParticipantReferralForm() {
  const { id } = useParams()
  const userCtx = useContext(UserContext)
  const location = useLocation()
  const navigate = useNavigate()
  const partnerRoles = [9, 10]

  const params = id.split("&")

  const refId = params[0]
  let pid = undefined

  if (params.length > 1) {
    pid = params[1].replace("pid=", "")
  }

  const query =
    !!pid && pid !== "not-set"
      ? REFERRAL_BY_IDSTAMP
      : REFERRAL_BY_IDSTAMP_NO_PARTICIPANT

  const [errorMessage, setErrorMessage] = useState("")
  const [referral, setReferral] = useState()
  const [refStatus, setRefStatus] = useState()
  const [refRating, setRefRating] = useState(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isLoadingRefStatus, setIsLoadingRefStatus] = useState(true)
  const [showEntityStatusUpdate, setShowEntityStatusUpdate] = useState(false)
  const [showRefStatusUpdate, setShowRefStatusUpdate] = useState(false)
  const [participantSetup, setParticipantSetup] = useState([])

  const ratingStatuses = ["Completed referral"]

  let referralRoute = ROUTES.REFERRALS
  let isParticipant = false

  if (
    location.pathname === ROUTES.PARTICIPANT_REFERRAL_ADD ||
    location.pathname.startsWith(
      ROUTES.PARTICIPANT_REFERRAL_EDIT.substring(
        0,
        ROUTES.PARTICIPANT_REFERRAL_EDIT.lastIndexOf("/")
      )
    )
  ) {
    referralRoute = ROUTES.PARTICIPANT_REFERRALS
    isParticipant = true
  }

  function GetParticipantTab(name) {
    if (participantSetup.length > 0) {
      const sectionSetup = participantSetup.filter((x) => x.name === name)[0]
      const sectionFields = Object.entries(sectionSetup.fields)

      return (
        <TabPanel header={sectionSetup.displayName} className="p-fluid">
          {sectionFields.map(([, field]) => {
            const ignoredField = field.field_type === "hidden"
            const label = field.field_label
            const dataFieldName = `${name}.${field.field_name}`

            if (ignoredField) {
              return null
            } else {
              return (
                <RefTextInput
                  name={dataFieldName}
                  label={label}
                  placeholderText=" "
                  isInitialLoad={isInitialLoad}
                  isLoading={isLoading}
                  disabled={true}
                  key={dataFieldName}
                />
              )
            }
          })}
        </TabPanel>
      )
    } else {
      return null
    }
  }

  function fixParticipantDetailValues(fieldSetup, dataset) {
    if (!!dataset) {
      const dateFields = fieldSetup.fields.filter(
        (field) =>
          field.field_type === "date" || field.field_type === "conditional_date"
      )

      dateFields.forEach((field) => {
        if (dataset[field.field_name] !== "") {
          dataset[field.field_name] = getLocalDateTimeFromIso(
            dataset[field.field_name],
            "MM/dd/yyyy"
          )
        }
      })
    }

    return dataset
  }

  const [getReferralExtraData] = useLazyQuery(REFERRAL_EXTRA_DETAIL, {
    fetchPolicy: "cache-and-network",
    variables: {
      organization_id: "",
    },
  })

  useQuery(query, {
    variables:
      !!pid && pid !== "not-set"
        ? {
            idStamp: refId,
            pid: pid,
            organization_id: userCtx.session.user.organization_id,
          }
        : {
            idStamp: refId,
            organization_id: userCtx.session.user.organization_id,
          },
    fetchPolicy: "network-only",
    onCompleted: async (result) => {
      try {
        if (result.referrals !== null && result.referrals.data !== null) {
          const referralsParsed = JSON.parse(result.referrals.data)
          const participantValuesParsed = !!result.participant
            ? JSON.parse(result.participant.values)
            : {
                participant__characteristics: undefined,
                participant__contact_information: undefined,
                participant__demographics: undefined,
              }

          let referralDetail = undefined
          let referralSession = undefined
          let referralCreator = undefined
          if (referralsParsed.length > 0) {
            referralDetail = referralsParsed.find(
              (block) => block.block_name === "referral"
            )
            referralSession = referralsParsed.find(
              (block) => block.block_name === "session"
            )
            referralCreator = referralsParsed.find(
              (block) => block.block_name === "staff"
            )

            if (!!referralSession) {
              const sessionDate = DateTime.fromISO(
                referralSession.values.session_date
              )
                .toLocal()
                .toLocaleString()

              referralSession.values.session_date_local = sessionDate
            }
          }

          const referral_extra = await getReferralExtraData({
            variables: {
              organization_id: referralDetail.organization_id,
            },
          })
          result.participant__demographics =
            referral_extra.data.participant__demographics
          result.referral_block = referral_extra.data.referral_block
          result.participant__characteristics =
            referral_extra.data.participant__characteristics
          result.participant__contact_information =
            referral_extra.data.participant__contact_information

          const refEntity = { status: "loading..." }

          if (referralDetail !== undefined) {
            //Fix dates
            referralDetail.values.referral_appointment_date =
              referralDetail.values.referral_appointment_date === ""
                ? ""
                : getLocalDateTimeFromIso(
                    referralDetail.values.referral_appointment_date,
                    DateTime.DATE_SHORT
                  )
            referralDetail.values.referral_appointment_time =
              referralDetail.values.referral_appointment_time === ""
                ? ""
                : getLocalDateTimeFromIso(
                    referralDetail.values.referral_appointment_time,
                    DateTime.TIME_WITH_SHORT_OFFSET
                  )

            setReferral({
              referral: referralDetail,
              referral_session: referralSession,
              referral_creator: referralCreator,
              referral_block: result.referral_block,
              refEntity: refEntity,
              participant: result.participant?.participant,
              pid_character: fixParticipantDetailValues(
                result.participant__characteristics,
                participantValuesParsed.participant__characteristics
              ),
              pid_contact:
                participantValuesParsed?.participant__contact_information,
              pid_demo: fixParticipantDetailValues(
                result.participant__demographics,
                participantValuesParsed?.participant__demographics
              ),
            })

            result.participant__contact_information = {
              ...result.participant__contact_information,
              name: "pid_contact",
              displayName: "Contact information",
            }

            result.participant__characteristics = {
              ...result.participant__characteristics,
              name: "pid_character",
              displayName: "Characteristics",
            }

            result.participant__demographics = {
              ...result.participant__demographics,
              name: "pid_demo",
              displayName: "Demographics",
            }

            result.participant__demographics.fields =
              result.participant__demographics.fields.filter(
                (x) => x.idField !== "demo_ssn"
              )

            setParticipantSetup([
              result.participant__contact_information,
              result.participant__characteristics,
              result.participant__demographics,
            ])

            let refPromises = []

            refPromises.push(
              refApiRefEntityStatusGet(userCtx.token, refId)
                .then((result) => {
                  setRefStatus(result)
                })
                .catch((ex) => {
                  console.log(ex)

                  if (ex.isAxiosError && ex.response !== undefined) {
                    setErrorMessage(ex.response.data)
                  } else {
                    setErrorMessage("Referral API: " + ex.message)
                  }
                })
            )

            refPromises.push(
              RefApiRefRatingGet(userCtx.token, refId)
                .then((result) => {
                  setRefRating(result)
                })
                .catch((ex) => {
                  console.log(ex)

                  if (ex.isAxiosError && ex.response !== undefined) {
                    setErrorMessage(ex.response.data)
                  } else {
                    setErrorMessage("Referral API: " + ex.message)
                  }
                })
            )

            //Wait for the Referral API calls to complete before continuing
            await Promise.all(refPromises).then((result) => {
              setIsInitialLoad(false)
              setIsLoading(false)
            })
          } else {
            setReferral(null)
          }
        } else {
          setReferral(null)
        }
        setIsInitialLoad(false)
        setIsLoading(false)
      } catch (ex) {
        LogError(ex)
        setErrorMessage(ex)
        setIsInitialLoad(false)
        setIsLoading(false)
      }
    },
    onError: (error) => {
      LogError(error)
      userCtx.setNotificationError("Unable to load referral detail")
      setIsInitialLoad(false)
      setReferral(null)
      setIsLoading(false)
      //Return to Notification listing URL.
      navigate(referralRoute)
    },
  })

  useEffect(() => {
    if (
      referral !== undefined &&
      refStatus !== undefined &&
      isLoadingRefStatus
    ) {
      setIsLoadingRefStatus(false)
      if (refStatus.referralId !== null) {
        referral.refEntity.status = refStatus.referralEntityStatus
      } else {
        referral.refEntity.status = "Pending"
      }
    }
  }, [referral, refStatus, isLoadingRefStatus])

  function HandleSubmit(values, setSubmitting) {
    console.log(values)
  }

  function updateRefEntityStatus(status, newReferralStatus, note) {
    referral.refEntity.status = status
    referral.referral.values.referral_status = newReferralStatus
    setRefStatus({ ...refStatus, referralEntityStatus: status })
    setIsLoadingRefStatus(true)
  }

  function NotesInput({ referral_block }) {
    const [field] = useField("referral.values.referral_notes")

    const blockField = !!referral_block
      ? referral_block?.fields.filter(
          (field) => field.idField === "referral_notes"
        )
      : []

    if (blockField.length > 0 && !blockField[0].field_type !== "hidden") {
      if (isLoading || isInitialLoad) {
        return null
      } else {
        return (
          <div className={styles.field}>
            <div>Referal notes</div>
            <div className="col-12 md:col-10">
              <InputTextareaRich
                name="referral.values.referral_notes"
                value={field.value}
                readOnly
                showHeader={false}
                className={"block"}
                style={{ height: "280px" }}
              />
            </div>
          </div>
        )
      }
    } else {
      return null
    }
  }

  const GetReferringDetail = ({ values }) => {
    return (
      <>
        <RefTextInput
          name={"referral_creator.organization_name"}
          label={"Referring organization"}
          placeholderText=""
          isInitialLoad={isInitialLoad}
          isLoading={isLoading}
          disabled={true}
        />

        <RefTextInput
          name={"referral_creator.staff_full_name"}
          label={"Referring staff"}
          placeholderText=""
          isInitialLoad={isInitialLoad}
          isLoading={isLoading}
          disabled={true}
        />

        <RefTextInput
          name={"referral_creator.staff_email_address"}
          label={"Referring staff e-mail"}
          placeholderText=""
          isInitialLoad={isInitialLoad}
          isLoading={isLoading}
          disabled={true}
        />

        <RefTextInput
          name={"referral_session.values.session_date_local"}
          label={"Referral date"}
          placeholderText=""
          isInitialLoad={isInitialLoad}
          isLoading={isLoading}
          disabled={true}
        />
      </>
    )
  }

  function GetReferralDetail({ values }) {
    let excludeFields = isParticipant ? [] : []
    excludeFields = [
      ...excludeFields,
      "referral_type",
      "referral_status",
      "referral_reason",
      "referral_type_other",
      "referral_entity_id",
      "referral_entity_name",
      "referral_address",
      "referral_city",
      "referral_country",
      "referral_phone",
      "referral_notified",
      "referral_state",
      "referral_zip_code",
      "referral_notes",
      "referral_taxonomy_service",
      "referral_taxonomy_service_other",
    ]

    const GetField = ({
      fieldPath,
      fieldName,
      fieldLabel,
      ignoreExclusion,
      showHidden,
    }) => {
      if (
        !ignoreExclusion &&
        excludeFields.some((field) => field === fieldName)
      ) {
        return null
      }

      if (!!values) {
        const field = values.referral_block?.fields.filter(
          (field) => field.idField === fieldName
        )

        if (
          !!field &&
          field.length > 0 &&
          (field[0].field_type !== "hidden" || !!showHidden)
        ) {
          return (
            <RefTextInput
              name={fieldPath}
              label={
                field[0].field_label !== "" ? field[0].field_label : fieldLabel
              }
              placeholderText=""
              isInitialLoad={isInitialLoad}
              isLoading={isLoading}
              disabled={true}
            />
          )
        } else {
          return null
        }
      } else {
        return (
          <RefTextInput
            name={fieldName}
            label={fieldLabel}
            placeholderText=""
            isInitialLoad={isInitialLoad}
            isLoading={isLoading}
            disabled={true}
          />
        )
      }
    }

    return (
      <TabPanel header="Referral detail" className="p-fluid">
        <div>
          <div
            style={{
              display: "inline-block",
              width: userCtx.session.user.staff_role < 9 ? "88%" : "100%",
            }}
          >
            <RefTextInput
              name="referral.values.referral_status"
              label="Referral status"
              placeholderText=""
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
          </div>
          {userCtx.session.user.staff_role < 9 && !isInitialLoad ? (
            <div style={{ display: "inline-block", width: "12%" }}>
              <Button
                label="Update status"
                icon="pi pi-pencil"
                type="button"
                className={refStyles.button}
                style={{ top: "10px", marginLeft: "10px" }}
                onClick={() => {
                  setShowRefStatusUpdate(true)
                }}
                loading={isLoading || isInitialLoad}
              />
            </div>
          ) : null}
        </div>

        <div>
          <div
            style={{
              display: "inline-block",
              width:
                userCtx.session.user.staff_role === 9 ||
                userCtx.session.user.staff_role === 10
                  ? "90%"
                  : "100%",
            }}
          >
            <RefTextInput
              name="refEntity.status"
              label="Partner status"
              placeholderText="Partner status"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
          </div>
          {userCtx.session.user.staff_role === 9 ||
          userCtx.session.user.staff_role === 10 ? (
            <div style={{ display: "inline-block", width: "10%" }}>
              <Button
                label="Update status"
                icon="pi pi-times"
                type="button"
                className={refStyles.button}
                style={{ top: "10px", marginLeft: "10px" }}
                onClick={() => {
                  setShowEntityStatusUpdate(true)
                }}
                loading={
                  isLoading ||
                  isInitialLoad ||
                  values.refEntity?.status === "loading..."
                }
              />
            </div>
          ) : null}
        </div>

        <GetReferringDetail values={values} />

        <GetField
          fieldPath="referral.values.referral_type"
          fieldName="referral_type"
          fieldLabel="Referral type"
          ignoreExclusion={true}
        />

        <GetField
          fieldPath="referral.values.referral_type_other"
          fieldName="referral_type_other"
          fieldLabel="Referral type Other"
          ignoreExclusion={true}
        />

        <RefTextInput
          name="referral.values.referral_taxonomy_service"
          label="Referral category"
          placeholderText="Unavailable"
          isInitialLoad={isInitialLoad}
          disabled={true}
        />

        {values?.referral.values.referral_taxonomy_service
          ?.toLowerCase()
          .includes("other") ? (
          <RefTextInput
            name="referral.values.referral_taxonomy_service_other"
            label="Referral category Other"
            isInitialLoad={isInitialLoad}
            disabled={true}
          />
        ) : null}

        {isParticipant ? (
          <>
            <GetField
              fieldPath="referral.values.referral_entity_name"
              fieldName="referral_entity_name"
              fieldLabel="Referral partner"
              ignoreExclusion={true}
              showHidden={true}
            />
            <GetField
              fieldPath="referral.values.referral_phone"
              fieldName="referral_phone"
              fieldLabel="Referral phone"
              ignoreExclusion={true}
              showHidden={true}
            />
            <GetField
              fieldPath="referral.values.referral_address"
              fieldName="referral_address"
              fieldLabel="Referral address"
              ignoreExclusion={true}
              showHidden={true}
            />
            <GetField
              fieldPath="referral.values.referral_city"
              fieldName="referral_city"
              fieldLabel="Referral city"
              ignoreExclusion={true}
              showHidden={true}
            />
            <GetField
              fieldPath="referral.values.referral_state"
              fieldName="referral_state"
              fieldLabel="Referral state"
              ignoreExclusion={true}
              showHidden={true}
            />
            <GetField
              fieldPath="referral.values.referral_country"
              fieldName="referral_country"
              fieldLabel="Referral country"
              ignoreExclusion={true}
              showHidden={true}
            />
          </>
        ) : (
          <>
            <RefTextInput
              name="participant.participant_name_first"
              label="Participant first name"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
            <RefTextInput
              name="participant.participant_name_last"
              label="Participant last name"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />

            <RefTextInput
              name="participant.participant_name_preferred"
              label="Participant preferred name"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
            <RefTextInput
              name="participant.participant_phone_primary"
              label="Primary phone"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
            <RefTextInput
              name="participant.participant_email_account"
              label="Primary email"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
            <RefTextInput
              name="participant.participant_city_primary"
              label="Primary city"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
            <RefTextInput
              name="participant.participant_state_primary"
              label="Primary state"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
            <RefTextInput
              name="participant.participant_zip_code_primary"
              label="Primary zip code"
              isInitialLoad={isInitialLoad}
              disabled={true}
            />
          </>
        )}

        <GetField
          fieldPath="referral.values.referral_reason"
          fieldName="referral_reason"
          fieldLabel="Reason for referral"
          ignoreExclusion={true}
        />

        <NotesInput referral_block={values?.referral_block} />

        {values?.referral_block?.fields?.map((field) => (
          <GetField
            fieldPath={`referral.values.${field.idField}`}
            fieldName={field.idField}
            fieldLabel={field.field_label}
            key={field.idField}
          />
        ))}
      </TabPanel>
    )
  }

  //If this is not a partner user, check if the participant in context is valid.
  if (!partnerRoles.includes(userCtx.session.user.staff_role)) {
    if (
      !userCtx.participant_id ||
      (referral &&
        referral?.referral?.participant_id !== userCtx.participant_id)
    ) {
      navigate(referralRoute)
    }
  }

  return (
    <>
      {ratingStatuses.some(
        (status) => status === referral?.referral?.values.referral_status
      ) ? (
        <ReferralRating rating={refRating} />
      ) : null}

      <h2>Referral detail</h2>
      <div>
        {errorMessage !== "" ? (
          <label className={styles.errFieldMsg}>{errorMessage}</label>
        ) : null}
      </div>
      {!!referral || isInitialLoad ? (
        <Formik
          initialValues={referral}
          enableReinitialize
          onSubmit={(values) => {
            HandleSubmit(values)
          }}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty, values, errors }) => (
            <>
              <ReferralEntityStatusPopup
                showEntityStatusUpdate={showEntityStatusUpdate}
                setShowEntityStatusUpdate={setShowEntityStatusUpdate}
                referralEntityId={
                  !!referral
                    ? referral.referral?.values?.referral_entity_id
                    : undefined
                }
                referralOrganizationId={referral?.referral?.organization_id}
                referralId={refId}
                setRefStatus={updateRefEntityStatus}
                referralCreatorStaffId={referral?.referral?.staff_id || ""}
                referralParticipantId={referral?.referral?.participant_id || ""}
                referralOrganizationID={
                  referral?.referral?.organization_id || ""
                }
                currentEntityStatus={values?.refEntity?.status}
                currentReferralStatus={
                  values?.referral?.values?.referral_status
                }
                currentReferralParticipantNote={
                  values?.referral?.values?.referral_participants_notes
                }
              />
              <ReferralStatusPopup
                showForm={showRefStatusUpdate}
                onHideForm={() => {
                  setShowRefStatusUpdate(false)
                }}
                onStatusChanged={(newStatus) => {
                  setShowRefStatusUpdate(false)

                  let newRef = { ...referral }
                  newRef.referral.values.referral_status = newStatus
                  setReferral(newRef)
                }}
                referralIdStamp={referral?.referral.idStamp}
                referralOrganizationId={referral?.referral.organization_id}
                referralStatus={referral?.referral.values.referral_status}
                referralEntityId={referral?.referral.values.referral_entity_id}
                referralParticipantsNotes={
                  referral?.referral.values.referral_participants_notes
                }
              />

              <Form onSubmit={handleSubmit} autoComplete="off">
                <div>
                  <TabView>
                    {GetReferralDetail({ values })}
                    {values ? GetParticipantTab("pid_demo") : null}
                    {values ? GetParticipantTab("pid_character") : null}
                    {values ? GetParticipantTab("pid_contact") : null}
                    <TabPanel header={"Notes"}>
                      {referral === undefined ? null : (
                        <ReferralNotes
                          referralId={refId}
                          referralEntityId={
                            referral?.referral?.values.referral_entity_id
                          }
                          loadAllMessages={true}
                          referralCreatorStaffId={
                            referral?.referral?.staff_id || ""
                          }
                          referralParticipantId={
                            referral?.referral?.participant_id || ""
                          }
                          referralOrganizationID={
                            referral?.referral?.organization_id || ""
                          }
                        />
                      )}
                    </TabPanel>
                  </TabView>

                  <div
                    className={
                      isParticipant
                        ? refStyles.participantfooter
                        : refStyles.footer
                    }
                  >
                    <div
                      className={
                        isParticipant
                          ? refStyles.participantfooter +
                            " " +
                            refStyles.buttonContainer
                          : refStyles.footer + " " + refStyles.buttonContainer
                      }
                    >
                      <Button
                        label="Cancel"
                        icon="pi pi-times"
                        type="button"
                        className={refStyles.button}
                        onClick={() => {
                          navigate(referralRoute)
                        }}
                        loading={isLoading || isInitialLoad}
                        style={{ width: "115px" }}
                      />
                    </div>
                  </div>
                </div>
              </Form>
            </>
          )}
        </Formik>
      ) : (
        <div style={{ color: "red" }}>Referral not found</div>
      )}
    </>
  )
}
